import { useMemo } from 'react';

import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';

import { useOpenSearchContext } from './useOpenSearchContext';
import { useUpdateSearchParams } from './useUpdateSearchParams';

interface UsePaginationOptions {
  padding?: number;
}

const usePagination = (options: UsePaginationOptions = {}) => {
  const { padding = 2 } = options;
  const { totalHits, pageSize, from, updateFrom } = useOpenSearchContext();
  const { updateSearchParams } = useUpdateSearchParams();

  const currentSize = pageSize || 20;
  const currentPage = Math.floor(from / currentSize);
  const totalPages = Math.ceil(totalHits / currentSize);

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  const pages = useMemo(() => {
    const lastPage = Math.max(0, totalPages - 1);
    const leftMargin = currentPage - padding < 0 ? padding - currentPage : 0;
    const rightMargin =
      currentPage + padding > lastPage ? currentPage - lastPage + padding : 0;

    const startPage = Math.max(0, currentPage - padding - rightMargin);
    const endPage = Math.min(
      totalPages - 1,
      currentPage + padding + leftMargin,
    );

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index,
    );
  }, [currentPage, totalPages, padding]);

  const refine = (page: number) => {
    if (page < 0 || page >= totalPages) return;

    const newFrom = page * currentSize;
    updateFrom(newFrom);
    updateSearchParams({ [PRODUCT_FILTER_ATTRIBUTES._PAGE]: String(page + 1) });
  };

  return {
    pages,
    currentPage,
    totalPages,
    isFirstPage,
    isLastPage,
    refine,
    canRefine: totalPages > 1,
  };
};

export { usePagination };
